import { useEffect, useState } from 'react';
import { Alert, Button, Descriptions, Modal, Spin, Tooltip } from 'antd';
import { LoadingOutlined, PhoneOutlined, SwapOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useMessageRedux from '@/store/message-redux';
import { takeoverAiCall, type AiCallVo } from '@/facilities/modules/aiCall';
import { getAiHotlineSettings } from '@/facilities/modules/aiHotlineSettings';
import { callerLabel, takeoverInProgress, takeoverStatusLabel } from './utils';
import type { BrowserPhone } from './browserPhone';
import { BrowserPhoneStatusPill } from './BrowserCallPanel';

interface Props {
  call: AiCallVo;
  /** the call as returned by the takeover request (transfer = requested) */
  onRequested: (next: AiCallVo) => void;
  /** where the takeover lands (Settings › AI Hotline); phone when unknown */
  target?: 'phone' | 'browser';
  phone?: BrowserPhone;
}

/**
 * "Take over" for a live call: confirm (shows the officer number from Settings), then the bot interrupts
 * the AI, says one hand-off sentence and dials the officer, who is bridged without any spoken brief.
 * While that runs the button turns into a status pill.
 */
export function TakeoverButton({ call, onRequested, target = 'phone', phone }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = useMessageRedux();
  const [open, setOpen] = useState(false);
  const [loadingNumber, setLoadingNumber] = useState(false);
  const [officer, setOfficer] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingNumber(true);
    setOfficer(null);
    getAiHotlineSettings()
      .then((res) => {
        if (!cancelled)
          setOfficer(String(res.data?.body?.transferPhoneNumber || '').trim());
      })
      .catch(() => {
        if (!cancelled) setOfficer('');
      })
      .finally(() => {
        if (!cancelled) setLoadingNumber(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (takeoverInProgress(call)) {
    return <TakeoverPill call={call} />;
  }
  const browser = target === 'browser';
  const browserReady = !!phone && phone.status === 'ready';

  const confirm = async () => {
    setSending(true);
    try {
      const res = await takeoverAiCall(call.id);
      const next: AiCallVo | null = res.data?.body || null;
      setOpen(false);
      message.success(t('hotline_takeoverSent'));
      if (next) onRequested(next);
    } catch (error: unknown) {
      message.error(String((error as { detail?: string })?.detail || error));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {browser && phone && <BrowserPhoneStatusPill phone={phone} />}
      <Button
        type="primary"
        size="small"
        icon={<SwapOutlined />}
        onClick={() => setOpen(true)}
        data-testid="takeover-button"
      >
        {t('hotline_takeover')}
      </Button>
      <Modal
        title={
          <span className="flex items-center gap-2">
            <PhoneOutlined />
            {t('hotline_takeoverTitle')}
          </span>
        }
        open={open}
        onCancel={() => !sending && setOpen(false)}
        onOk={confirm}
        okText={t('hotline_takeoverConfirm')}
        okButtonProps={{
          icon: <SwapOutlined />,
          loading: sending,
          disabled: browser ? !browserReady : loadingNumber || !officer,
        }}
        cancelText={t('cancel')}
        destroyOnClose
      >
        <p className="text-[#00000073] dark:text-[#ffffff73] mb-3">
          {t(browser ? 'hotline_takeoverHintBrowser' : 'hotline_takeoverHint')}
        </p>
        <Descriptions
          size="small"
          column={1}
          colon={false}
          styles={{ label: { width: 120, color: '#00000073' } }}
          items={[
            {
              key: 'caller',
              label: t('hotline_caller'),
              children: (
                <span>
                  {callerLabel(call)}
                  {call.callerNumber && call.formData?.fullName
                    ? ` · ${call.callerNumber}`
                    : ''}
                </span>
              ),
            },
            browser
              ? {
                  key: 'browser',
                  label: t('hotline_takeoverAnsweringIn'),
                  children: (
                    <span className="flex items-center gap-2">
                      {t('hotline_thisBrowser')}
                      {phone && <BrowserPhoneStatusPill phone={phone} />}
                    </span>
                  ),
                }
              : {
                  key: 'officer',
                  label: t('hotline_takeoverOfficer'),
                  children: loadingNumber ? (
                    <Spin size="small" />
                  ) : officer ? (
                    <span className="font-mono">{officer}</span>
                  ) : (
                    <span className="text-[#ff4d4f]">-</span>
                  ),
                },
          ]}
        />
        {browser && !browserReady && (
          <Alert
            className="mt-3"
            type="warning"
            showIcon
            message={t('hotline_browserNotReady')}
            action={
              <Button size="small" onClick={() => navigate('/settings')}>
                {t('hotline_openSettings')}
              </Button>
            }
          />
        )}
        {!browser && !loadingNumber && officer === '' && (
          <Alert
            className="mt-3"
            type="warning"
            showIcon
            message={t('hotline_takeoverNoOfficer')}
            action={
              <Button size="small" onClick={() => navigate('/settings')}>
                {t('hotline_openSettings')}
              </Button>
            }
          />
        )}
      </Modal>
    </>
  );
}

export function TakeoverPill({ call }: { call: AiCallVo }) {
  const { t } = useTranslation();
  const who = call.transfer?.requestedByName;
  return (
    <Tooltip title={who ? t('hotline_takeoverBy', { name: who }) : undefined}>
      <span className="live-takeover" data-testid="takeover-pill">
        <LoadingOutlined spin />
        {takeoverStatusLabel(call, t)}
      </span>
    </Tooltip>
  );
}
